import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/App';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Navigation from '@/components/Navigation';
import { CheckCircle, Loader } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('checking');
  const [bookingId, setBookingId] = useState(null);
  const [attempts, setAttempts] = useState(0);

  const pollPaymentStatus = useCallback(async () => {
    if (attempts >= 5) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.status === 'paid') {
        setStatus('success');
        setBookingId(response.data.booking_id);
      } else {
        setTimeout(() => setAttempts((prev) => prev + 1), 2000);
      }
    } catch (error) {
      console.error('Failed to check payment status:', error);
      setStatus('error');
    }
  }, [attempts, sessionId, token]);

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    }
  }, [sessionId, attempts, pollPaymentStatus]);

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation />

      <div className="max-w-2xl mx-auto px-6 py-20 mt-16 text-center">
        {status === 'checking' && (
          <>
            <Loader className="w-16 h-16 mx-auto mb-6 text-blue-600 animate-spin" />
            <h1 className="text-3xl font-bold tracking-tight text-black mb-4" style={{fontFamily: 'Outfit'}}>
              Processing Payment...
            </h1>
            <p className="text-slate-600">Please wait while we confirm your payment</p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle className="w-16 h-16 mx-auto mb-6 text-green-600" />
            <h1 className="text-3xl font-bold tracking-tight text-black mb-4" style={{fontFamily: 'Outfit'}}>
              Payment Successful!
            </h1>
            <p className="text-slate-600 mb-8">
              Your booking has been confirmed. The consultant will contact you shortly.
            </p>
            <button
              onClick={() => navigate('/bookings')}
              className="px-8 py-3 bg-black text-white rounded-sm hover:bg-slate-800 transition-all"
            >
              View My Bookings
            </button>
          </>
        )}

        {(status === 'error' || status === 'timeout') && (
          <>
            <div className="w-16 h-16 mx-auto mb-6 bg-red-100 rounded-full flex items-center justify-center">
              <span className="text-red-600 text-3xl">!</span>
            </div>
            <h1 className="text-3xl font-bold tracking-tight text-black mb-4" style={{fontFamily: 'Outfit'}}>
              Payment Status Check Failed
            </h1>
            <p className="text-slate-600 mb-8">
              We couldn't verify your payment status. Please check your bookings or contact support.
            </p>
            <button
              onClick={() => navigate('/bookings')}
              className="px-8 py-3 bg-black text-white rounded-sm hover:bg-slate-800 transition-all"
            >
              Go to Bookings
            </button>
          </>
        )}
      </div>
    </div>
  );
}